import { InvalidSchemaError } from '../../../errors'
import type { SchemaSource } from '../types'
import { type AnyZod, inspect, shapeEntries } from './introspect'

// The declared type surface, enforced. Sibling of ./assert-object-schema.ts — that one checks the CONTAINER
// is a z.object, this one checks what is declared INSIDE it, at every depth.
//
// Why it exists: the runtime reads a zod declaration to decide the published type, the widget the console
// draws, and which coercion runs before `execute`. Everything it cannot classify used to fall through to
// `STRING`. That fallback is worse than it looks, because it fails in two places at once — the parameter is
// published as text (a list stops being a list, a toggle becomes a text box) AND it drops out of the
// coercion layer, so the string values a flow supplies are never converted. A `z.boolean().catch(false)`
// bound to a template therefore hands `execute` `false` for an explicit `'true'`: exactly the legacy boolean
// defect that v2's coercion layer exists to close.
//
// So an unrecognised declaration must be an ERROR, not a guess. The supported set below is closed and
// documented (service-code-format/02-zod-types.md); a zod release that adds a modifier cannot silently
// degrade a parameter, because the new name simply is not in it.
//
// Runs at REGISTRATION, while the service module is being required — the same moment, and for the same
// reason, as the object-container check: it fires on the first call the CLI or console makes against the
// extension, including the `buildServiceDefinition` behind `flowrunner deploy`, so a bad declaration stops
// the deploy instead of shipping.

/**
 * Slots held to the documented surface — everything an AUTHOR writes.
 *
 * The runtime's own SYSTEM schemes (`kind: 'system method'`) are deliberately exempt: they model the wire
 * contract of a platform call rather than a rendered form, and legitimately carry `z.unknown()` bodies and
 * loose records. So is the anonymous slot used for per-invocation payload parsing, which re-wraps a schema
 * that was already checked where it was declared.
 */
const GUARDED_KINDS = new Set(['action', 'dictionary', 'polling trigger', 'realtime trigger', 'extension'])

/**
 * The core types the runtime can publish, render and coerce.
 *
 * `any` / `unknown` are in deliberately: they declare nothing, the pipeline passes them through untouched,
 * and that is a coherent thing for an author to want. Everything absent is absent on purpose — `tuple` and
 * `intersection` have no single widget, and a `catch` is refused with its own message below.
 *
 * `union` is not listed because it never reaches this check as itself: `inspect()` resolves it to its first
 * branch, so `resolvedType` is that branch's type. Its branches are each held to this same set — see
 * `assertField` — so a union containing a `z.tuple()` is still refused.
 */
const SUPPORTED_TYPES = new Set([
  'string', 'number', 'bigint', 'boolean', 'date', 'enum', 'literal',
  'object', 'array', 'record',
  // `z.union([T, z.null()])` is the union-shaped spelling of `.nullable()`, which is supported — refusing one
  // and not the other is a distinction without a difference. It is also a branch of zod's own `z.json()`.
  'null',
  'any', 'unknown',
])

// Named in the "supported" half of an error message, in the spelling an author writes. Kept in step with
// `SUPPORTED_TYPES` above and with the table in service-code-format/02-zod-types.md — an author who hits this
// error reads the message, then the doc, and a type missing from either reads as one they may not use.
const SUPPORTED_SPELLING = 'z.string(), z.number(), z.bigint(), z.boolean(), z.date(), z.enum([…]), ' +
  'z.literal(…), z.object({ … }), z.array(…), z.record(…), z.union([…]), z.null(), z.any(), z.unknown()'

const MODIFIER_SPELLING = '.optional(), .nullable(), .nullish(), .default(v), .prefault(v), .readonly(), ' +
  '.nonoptional(), .pipe(…)/.transform(…) and z.lazy(…)'

// `action "sendMessage": `params.tags[]`` — enough for an author to find the declaration without a stack trace.
function subjectOf(source: SchemaSource, path: string): string {
  const kind = source.kind ?? 'extension'
  const subject = source.itemId ? `${ kind } "${ source.itemId }": ` : ''

  return `${ subject }\`${ path }\``
}

function refuse(message: string, source: SchemaSource, path: string, received: string): never {
  throw new InvalidSchemaError(
    `[flow-extension:${ source.serviceId }] ${ message }`,
    { serviceId: source.serviceId, kind: source.kind ?? 'extension', itemId: source.itemId, slot: source.slot, path, received }
  )
}

/**
 * `.catch(v)` gets its own refusal, because the fix is not "use a supported type" — the author's intent is
 * fine and the spelling is one character away.
 *
 * It is refused rather than unwrapped on purpose. Unwrapping would restore the published type and the
 * coercion, but the declaration would still SWALLOW invalid input: zod replaces anything that fails with the
 * fallback, so a value the user actually supplied is silently discarded instead of being reported back. No
 * parameter should do that. `.default(v)` expresses the same intent honestly — it supplies the fallback when
 * the value is ABSENT, and still rejects one that is present and wrong.
 */
function refuseCatch(source: SchemaSource, path: string): never {
  refuse(
    `${ subjectOf(source, path) } uses \`.catch(…)\`, which a parameter may not: it replaces an invalid value` +
      ' with the fallback instead of reporting it, and drops the field out of type coercion.' +
      ' Use `.default(v)` — same fallback when the value is absent, and a supplied-but-invalid one still fails.',
    source, path, 'catch'
  )
}

function refuseType(source: SchemaSource, path: string, type: string | undefined): never {
  const received = type ? `z.${ type }()` : 'a value that is not a zod schema'

  refuse(
    `${ subjectOf(source, path) } declares ${ received }, which the runtime cannot publish as a parameter` +
      ' — it has no widget, no published type and no coercion for it.' +
      ` Supported: ${ SUPPORTED_SPELLING }; each may carry ${ MODIFIER_SPELLING }.`,
    source, path, type ?? 'unknown'
  )
}

// One declared field, then whatever it contains. `seen` stops a recursive `z.lazy()` from walking forever.
function assertField(schema: AnyZod | undefined, source: SchemaSource, path: string, seen: Set<unknown>): void {
  if (!schema || seen.has(schema)) {
    return
  }

  seen.add(schema)

  const ins = inspect(schema)

  if (ins.resolvedType === 'catch') {
    refuseCatch(source, path)
  }

  if (!ins.resolvedType || !SUPPORTED_TYPES.has(ins.resolvedType)) {
    refuseType(source, path, ins.resolvedType)
  }

  // Every branch of a union is a declaration in its own right. The union itself already passed the check
  // above AS ITS FIRST BRANCH, which says nothing about the others.
  if (ins.branches) {
    // Numbered, because the reported path is otherwise the field — and "declares z.tuple()" against a field
    // the author wrote as a union names a type they cannot find in their own declaration.
    ins.branches.forEach((branch, index) => assertField(branch, source, `${ path }|branch ${ index + 1 }`, seen))
  }

  const def = ins.core._zod?.def ?? {}

  // A container's contents are declarations in their own right and are held to the same rule — the defect
  // this guards against is no less silent one level down.
  if (ins.resolvedType === 'array') {
    assertField(def.element as AnyZod | undefined, source, `${ path }[]`, seen)
  }

  if (ins.resolvedType === 'record') {
    assertField(def.valueType as AnyZod | undefined, source, `${ path }{}`, seen)
  }

  if (ins.resolvedType === 'object') {
    for (const [name, property] of shapeEntries(ins.core)) {
      assertField(property, source, `${ path }.${ name }`, seen)
    }
  }
}

/**
 * Throws unless every field `scheme` declares, at every depth, is something the runtime can publish, render
 * and coerce. `undefined` passes — an undeclared slot is legal everywhere.
 *
 * Only author-declared slots are checked (see `GUARDED_KINDS`).
 */
export function assertSupportedSchema(scheme: unknown, source: SchemaSource): void {
  if (scheme == null || !source.kind || !GUARDED_KINDS.has(source.kind)) {
    return
  }

  const slot = source.slot ?? 'schema'
  const seen = new Set<unknown>()

  for (const [name, field] of shapeEntries(scheme as AnyZod)) {
    assertField(field, source, `${ slot }.${ name }`, seen)
  }
}
